import { debounce } from 'lodash';
import React from 'react';
import { SortingRule } from '@components/table';
import { userSearchDebounce } from '../components/hooks/use-search';
import { useWrapRequest } from 'use-wrap-request';
import { useSearchReducer } from '../components/hooks/useSearchReducer';
import { Api } from '../core/api';
import { CommunityVisibility } from '@api/ChallengeClient';
import { injectTSDI } from '../core/tsdi';

export function useCommunityStore() {
    const { challengeClient } = injectTSDI(Api);

    const [visibilityFilter, setVisibilityFilter] = React.useState<
        CommunityVisibility[]
    >([]);

    const [sorting, setSorting] = React.useState<
        SortingRule | SortingRule[] | undefined
    >(undefined);

    React.useEffect(() => {
        if (sorting) {
            dispatch({ type: 'sort', sort: sorting });
        }
    }, [sorting]);

    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0
    });
    const { setSearchTerm } = userSearchDebounce(dispatch);

    const communities = useWrapRequest(
        async (
            searchParams?: QueryParamsDto,
            visibilityFilter?: CommunityVisibility[]
        ) => {
            const query = {
                page: searchParams?.page || 0,
                size: searchParams?.size || 30,
                search: searchParams?.search,
                sort: searchParams?.sort,
                ...(visibilityFilter && visibilityFilter.length > 0
                    ? { visibility: visibilityFilter[0] } // API only accepts single visibility
                    : {})
            };

            const result =
                await challengeClient.support.communitySupportControllerGetPage(
                    query,
                    {}
                );

            return {
                ...result.data,
                content: result.data.content || []
            };
        },
        {
            defaultData: {
                content: [],
                totalElements: 0,
                number: 0,
                size: 30,
                totalPages: 0
            }
        }
    );

    const debouncedAPICall = React.useCallback(
        debounce(
            (
                searchParams?: QueryParamsDto,
                visibilityFilter?: CommunityVisibility[]
            ) => {
                communities.request([searchParams, visibilityFilter]);
            },
            100
        ),
        [communities]
    );

    React.useEffect(() => {
        debouncedAPICall(searchParams, visibilityFilter);
    }, [searchParams, visibilityFilter, debouncedAPICall]);

    return {
        communities,
        searchParams,
        visibilityFilter,
        sorting,
        setSorting,
        setVisibilityFilter,
        setSearchTerm,
        dispatch
    };
}
